import { useEffect, useRef, useState } from 'react';
import { confirmSignup } from '../../api/plans';
import { apiErrorMessage } from '../../api/errorMessage.ts';
import { useT } from '../../i18n/i18n.tsx';
import { Button, InlineAlert, Spinner } from '../../ui';
import './LoginDialog.css';

/**
 * MVP-278 email verification. `/signup/confirm?token=…`, the link in the confirmation mail. Confirms
 * once on arrival, then sends the person to sign in with their password — the server hands out no
 * JWT for a link alone.
 */
export function SignupConfirmScreen({ token }: { token: string }) {
  const t = useT();
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null);
  // A link works ONCE: StrictMode's second effect run must not spend it again and show the refusal.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    confirmSignup(token)
      .then(() => setResult({ ok: true, message: t('auth.confirm.done') }))
      .catch((err: unknown) => setResult({ ok: false, message: apiErrorMessage(err, t('auth.confirm.failed'), t) }));
  }, [token, t]);

  return (
    <main className="login-screen">
      <section className="login-dialog" aria-labelledby="confirm-title">
        <div className="login-dialog__heading">
          <img className="login-dialog__logo" src="/logo-rms.png" alt={t('brand.logoAlt')} width={456} height={194} />
          <h1 id="confirm-title" className="login-dialog__title text-title">
            RMS Karaoke
          </h1>
        </div>
        {result ? (
          <InlineAlert variant={result.ok ? 'ok' : 'danger'}>{result.message}</InlineAlert>
        ) : (
          <p className="text-body">
            <Spinner label={t('common.loading')} /> {t('auth.confirm.checking')}
          </p>
        )}
        {result && (
          <Button variant="primary" size="lg" fullWidth onClick={() => window.location.assign('/')}>
            {t('auth.signup.goToSignIn')}
          </Button>
        )}
      </section>
    </main>
  );
}
